import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import { Box, IconButton as MuiIconButton, InputBase } from "@mui/material";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCallback, useRef, useState } from "react";

import { MAX_FILE_SIZE, SUPPORTED_FILE_TYPES } from "./constants";

export interface ChatInputProps {
  onSend: (message: string) => void;
  onFileSelect?: (file: File) => void;
  disabled?: boolean;
}

/**
 * Chat input component with text field, file upload, and send button.
 */
export function ChatInput(props: ChatInputProps) {
  const { onSend, onFileSelect, disabled = false } = props;
  const [message, setMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    if (message.trim()) {
      onSend(message.trim());
      setMessage("");
    }
  }, [message, onSend]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  }, []);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        showErrorToast("File size must be less than 10MB");
        return;
      }

      // Validate file type
      if (!SUPPORTED_FILE_TYPES.includes(file.type as (typeof SUPPORTED_FILE_TYPES)[number])) {
        showErrorToast("Unsupported file type. Please upload images, PDFs, or Word documents.");
        return;
      }

      onFileSelect?.(file);

      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [onFileSelect]
  );

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 3,
        paddingTop: 3,
        paddingRight: 3,
        paddingBottom: 3,
        paddingLeft: 5,
        borderRadius: theme.borderRadius?.xLarge,
        border: `1px solid ${theme.palette.border?.base}`,
        backgroundColor: theme.palette.background.paper,
      })}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={SUPPORTED_FILE_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* File attachment button */}
      {onFileSelect && (
        <MuiIconButton
          aria-label="Attach file"
          disabled={disabled}
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            padding: "5px",
            "&:hover": {
              color: theme.palette.text.primary,
            },
            "&.Mui-disabled": {
              color: theme.palette.base?.inactive,
            },
          })}
          onClick={handleFileClick}
        >
          <AttachFileIcon sx={{ fontSize: "18px" }} />
        </MuiIconButton>
      )}

      <InputBase
        fullWidth
        placeholder="Type your message..."
        value={message}
        disabled={disabled}
        autoComplete="off"
        spellCheck="true"
        inputProps={{
          "aria-label": "Chat message",
          maxLength: 5000,
        }}
        sx={(theme) => ({
          ...theme.typography.body2, // 14px - "small" per design system
        })}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
      />
      <MuiIconButton
        aria-label="Send message"
        disabled={disabled || !message.trim()}
        sx={(theme) => ({
          backgroundColor: theme.palette.primary.main,
          color: "white",
          borderRadius: theme.borderRadius?.xLarge,
          padding: "5px",
          minWidth: "24px",
          minHeight: "24px",
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
          "&.Mui-disabled": {
            backgroundColor: theme.palette.base?.inactive,
            color: "white",
          },
        })}
        onClick={handleSend}
      >
        <SendIcon sx={{ fontSize: "14px" }} />
      </MuiIconButton>
    </Box>
  );
}
